import { useMemo, useState, type ReactNode } from 'react';
import { Button } from '@reflex/ui/components/ui/button';
import { ChevronDown, Menu, MoreVertical } from 'lucide-react';
import { PanelHeader } from '@reflex/ui/components/layout/PanelHeader';
import { PanelTitle } from '@reflex/ui/components/layout/Typography';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@reflex/ui/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@reflex/ui/components/ui/tooltip';
import { cn } from '@reflex/ui/lib/utils';
import { AgentActivityIcon } from '@/components/status/agent/AgentActivityIcon';
import { EditableAgentName } from '@/components/agent/EditableAgentName';
import { AgentHeaderIdentityChips } from './AgentHeaderIdentityChips';
import { MobileIdentitySheet } from './MobileIdentitySheet';
import { useAgentAccess } from '@/hooks/agents/useAgentAccess';
import {
  useAgentActions,
  isAgentHeaderInlineActionKey,
  isAgentHeaderOverflowActionKey,
  isAgentHeaderBeforeLabelsAction,
  isAgentHeaderInlinePluginAction,
  AgentActionMenuItems,
  AgentActionIconControl,
  AgentActionDialogs,
} from '@/components/agent/agent-actions';
import type { Agent, AgentActivity } from '@/lib/types';
import { ACTIVE_STATUSES } from '@/lib/types';
import type { ResolvedAgentAction, ResolvedAgentHeaderBadge } from '@/lib/plugin-registry';

interface AgentHeaderBarProps {
  agent: Agent;
  activity: AgentActivity;
  hideActions?: boolean;
  /**
   * Whether the surface hosting this header binds the session hotkeys. Passed
   * through so the menu only shows a chord hint where the chord is live.
   */
  hotkeysBound?: boolean;
  agentActions?: ResolvedAgentAction[];
  agentHeaderBadges: ResolvedAgentHeaderBadge[];
  onToggleSidebar?: () => void;
  /** Show the sidebar toggle at every viewport (sessions dock); default mobile only. */
  sidebarToggleAlwaysVisible?: boolean;
  onArchived?: () => void;
  /** Mobile-only second row (view strip + actions). Hidden from `md` up. */
  mobileSubHeader?: ReactNode;
}

/**
 * Title row for the agent stream: activity, name, identity chips, the inline
 * icons (Share, then Report bug, then Mark as / Stop), and the fixed More
 * menu (Launch again / Save as persona / Delete). Other pinable action icons
 * live on the right rail.
 *
 * Below `md` the title opens a sheet with the full name and identity tags
 * (rename lives there). A second row (`mobileSubHeader`) holds the view
 * strip and actions trigger. Details still opens the full panel.
 */
export function AgentHeaderBar({
  agent,
  activity,
  hideActions,
  hotkeysBound,
  agentActions = [],
  agentHeaderBadges,
  onToggleSidebar,
  sidebarToggleAlwaysVisible,
  onArchived,
  mobileSubHeader,
}: AgentHeaderBarProps) {
  const agentAccess = useAgentAccess(agent);
  const actions = useAgentActions(agent, { onArchived, agentActions, activity, hotkeysBound });
  const [openDialogKey, setOpenDialogKey] = useState<string | null>(null);
  const [identityOpen, setIdentityOpen] = useState(false);
  const inlineActions = useMemo(() => {
    const plugins = actions.filter((action) => isAgentHeaderInlinePluginAction(action));
    const host = actions.filter((action) => isAgentHeaderInlineActionKey(action.key));
    // Share is the innermost inline icon, immediately left of the plugin slot
    // (Report bug); the rest of the host chrome (Stop / Mark as) trails right.
    const share = host.filter((action) => action.key === 'share');
    const rest = host.filter((action) => action.key !== 'share');
    return [...share, ...plugins, ...rest];
  }, [actions]);
  const overflowActions = useMemo(
    () => actions.filter((action) => isAgentHeaderOverflowActionKey(action.key)),
    [actions],
  );
  const beforeLabelsActions = useMemo(
    () => actions.filter((action) => isAgentHeaderBeforeLabelsAction(action)),
    [actions],
  );
  const dialogActions = useMemo(
    () => [...inlineActions, ...overflowActions, ...beforeLabelsActions],
    [inlineActions, overflowActions, beforeLabelsActions],
  );
  // Mark as / Share stay as desktop header icons (`hidden md:flex` below) and
  // also land in the ⋮ menu with `md:hidden`, so mobile never loses them. The
  // Tailwind breakpoint is the source of truth — do not gate on useIsDesktop
  // here, which can disagree with the CSS layout the user is looking at.
  const showMoreMenu = !hideActions && (inlineActions.length > 0 || overflowActions.length > 0);
  const showAccentLine = ACTIVE_STATUSES.has(agent.status);

  return (
    <>
      <PanelHeader
        // Match the vertical tab rail: no right padding / inter-slot gap from
        // md up, and a w-9 column for the More control so it shares the rail's
        // centerline. Mobile is flush to the screen edges.
        // Opaque document surface (not glass) so this reads as the session
        // title bar beside the quieter session-dock chrome.
        className={cn(
          showAccentLine && !mobileSubHeader ? 'border-b-0' : undefined,
          'gap-0 h-11 px-0 md:px-4 md:pr-0 bg-background backdrop-blur-none border-border/50',
        )}
        leftClassName="gap-1.5 md:gap-2.5"
        rightClassName="gap-0"
        data-testid="agent-header-bar"
        left={
          <>
            {onToggleSidebar && (
              <Button
                variant="ghost"
                size="icon-sm"
                className={cn(
                  'shrink-0 h-11 w-11 md:h-8 md:w-8',
                  !sidebarToggleAlwaysVisible && 'md:hidden',
                )}
                onClick={onToggleSidebar}
                aria-label={sidebarToggleAlwaysVisible ? 'Toggle sidebar' : 'Open sidebar'}
              >
                <Menu className="h-5 w-5 md:h-4 md:w-4" />
              </Button>
            )}
            <AgentActivityIcon activity={activity} mark={agent.mark} className="h-4 w-4 shrink-0" />
            <div className="min-w-0 flex-1 pl-1 md:pl-0">
              <div
                className="flex w-fit max-w-full min-w-0 items-center gap-2 overflow-hidden"
                data-testid="agent-header-breadcrumb"
              >
                <button
                  type="button"
                  className={cn(
                    'inline-flex min-w-0 max-w-full items-center gap-1.5 overflow-hidden rounded-sm',
                    'text-left md:hidden',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  )}
                  onClick={() => setIdentityOpen(true)}
                  aria-label="Open title and tags"
                  data-testid="agent-header-open-identity"
                >
                  <PanelTitle
                    as="span"
                    size="sm"
                    className="min-w-0 truncate font-semibold tracking-tight"
                  >
                    {agent.name}
                  </PanelTitle>
                  <ChevronDown className="size-3.5 shrink-0 opacity-60" aria-hidden="true" />
                </button>
                <span
                  className="hidden min-w-0 shrink overflow-hidden md:inline-flex"
                  data-testid="agent-header-title"
                >
                  <EditableAgentName
                    agentId={agent.id}
                    name={agent.name}
                    disabled={hideActions || agentAccess.isReadOnly}
                    as="span"
                    size="default"
                    className="min-w-0 font-semibold tracking-tight"
                  />
                </span>
                <div className="hidden md:contents">
                  <AgentHeaderIdentityChips
                    agent={agent}
                    agentHeaderBadges={agentHeaderBadges}
                    hideActions={hideActions}
                    beforeLabels={
                      !hideActions && beforeLabelsActions.length > 0 ? (
                        <>
                          {beforeLabelsActions.map((action) => (
                            <AgentActionIconControl
                              key={action.key}
                              action={action}
                              onOpenDialog={(key) => setOpenDialogKey(key)}
                            />
                          ))}
                        </>
                      ) : null
                    }
                  />
                </div>
              </div>
            </div>
          </>
        }
        right={
          <div className="flex h-11 shrink-0 items-center" data-testid="agent-header-actions">
            {!hideActions && inlineActions.length > 0 ? (
              <div
                className="hidden md:flex items-center gap-0.5 pr-1"
                data-testid="agent-header-inline-actions"
              >
                {inlineActions.map((action) => (
                  <AgentActionIconControl
                    key={action.key}
                    action={action}
                    onOpenDialog={(key) => setOpenDialogKey(key)}
                  />
                ))}
              </div>
            ) : null}
            <div
              className="flex h-11 w-11 md:w-9 shrink-0 items-center justify-center md:border-l border-border/30"
              data-testid="agent-header-rail-slot"
            >
              {showMoreMenu ? (
                <DropdownMenu>
                  <Tooltip delayDuration={200}>
                    <TooltipTrigger asChild>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="toolbar-icon"
                          className="text-muted-foreground h-11 w-11 md:h-8 md:w-8"
                          aria-label="More actions"
                          data-tutorial-id="agent-actions-menu"
                          data-testid="agent-header-more-actions"
                        >
                          <MoreVertical className="h-5 w-5 md:h-4 md:w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                    </TooltipTrigger>
                    <TooltipContent>More actions</TooltipContent>
                  </Tooltip>
                  <DropdownMenuContent align="end" data-tutorial-id="agent-actions-menu-content">
                    {inlineActions.length > 0 ? (
                      <AgentActionMenuItems
                        actions={inlineActions}
                        className="md:hidden"
                        onSelectDialog={(action) => setOpenDialogKey(action.key)}
                      />
                    ) : null}
                    {inlineActions.length > 0 && overflowActions.length > 0 ? (
                      <DropdownMenuSeparator className="md:hidden" />
                    ) : null}
                    {overflowActions.length > 0 ? (
                      <AgentActionMenuItems
                        actions={overflowActions}
                        onSelectDialog={(action) => setOpenDialogKey(action.key)}
                      />
                    ) : null}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null}
            </div>
          </div>
        }
      />
      {mobileSubHeader ? (
        <div className="md:hidden" data-testid="agent-header-mobile-subheader">
          {mobileSubHeader}
        </div>
      ) : null}
      {showAccentLine ? <div className="accent-line" /> : null}
      <MobileIdentitySheet
        open={identityOpen}
        onOpenChange={setIdentityOpen}
        title={
          <div className="flex items-start gap-2">
            <AgentActivityIcon
              activity={activity}
              mark={agent.mark}
              className="mt-0.5 h-4 w-4 shrink-0"
            />
            <EditableAgentName
              agentId={agent.id}
              name={agent.name}
              disabled={hideActions || agentAccess.isReadOnly}
              as="span"
              size="sm"
              className="min-w-0 whitespace-normal"
            />
          </div>
        }
        details={
          <AgentHeaderIdentityChips
            agent={agent}
            agentHeaderBadges={agentHeaderBadges}
            hideActions={hideActions}
            wrap
            beforeLabels={
              !hideActions && beforeLabelsActions.length > 0 ? (
                <>
                  {beforeLabelsActions.map((action) => (
                    <AgentActionIconControl
                      key={action.key}
                      action={action}
                      onOpenDialog={(key) => setOpenDialogKey(key)}
                    />
                  ))}
                </>
              ) : null
            }
          />
        }
      />
      {!hideActions ? (
        <AgentActionDialogs
          actions={dialogActions}
          agent={agent}
          openKey={openDialogKey}
          onOpenKeyChange={setOpenDialogKey}
        />
      ) : null}
    </>
  );
}
